"use client";

import { useState, useEffect } from "react";
import { X, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";

interface RequestPayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableBalance: number;
  walletAddress: string | null;
  onSuccess: () => void;
}

interface PayoutResult {
  payoutId: string;
  amount: number;
  commissionCount: number;
}

export default function RequestPayoutModal({
  isOpen,
  onClose,
  availableBalance,
  walletAddress,
  onSuccess,
}: RequestPayoutModalProps) {
  const t = useT();
  const [destinationAddress, setDestinationAddress] = useState(walletAddress || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<PayoutResult | null>(null);

  // Sync walletAddress when modal opens (Privy may load after component mount)
  useEffect(() => {
    if (isOpen && walletAddress && !destinationAddress) {
      setDestinationAddress(walletAddress);
    }
  }, [isOpen, walletAddress]);

  const isValidAddress = /^0x[a-fA-F0-9]{40}$/.test(destinationAddress);

  const handleSubmit = async () => {
    if (!isValidAddress) {
      setError(t("aff.payout.invalidAddressError"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch("/api/referral/payout/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ destinationAddress }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t("aff.payout.failed"));
      }

      setSuccess({
        payoutId: data.payout.id,
        amount: data.payout.amount,
        commissionCount: data.payout.commissionCount,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("aff.payout.failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDestinationAddress(walletAddress || "");
    setError(null);
    setSuccess(null);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#1A1A1A]">
          <h2 className="font-monument text-lg text-gray-900 dark:text-white">
            {t("aff.payout.title")}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-[5px] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#25252B] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {success ? (
            /* Success View */
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-14 h-14 rounded-[5px] bg-emerald-500/10 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-inter-tight font-bold text-gray-900 dark:text-white mb-1">
                {t("aff.payout.requested")}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t("aff.payout.pendingApproval")}
              </p>

              <div className="w-full bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-4 space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t("aff.payout.amount")}</span>
                  <span className="text-sm font-mono font-bold text-emerald-500">
                    ${success.amount.toFixed(4)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t("aff.payout.commissions")}</span>
                  <span className="text-sm font-mono text-gray-900 dark:text-white">
                    {success.commissionCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t("aff.payout.status")}</span>
                  <span className="text-xs font-inter-tight font-semibold px-2 py-0.5 rounded-[5px] bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                    {t("aff.payout.statusPending")}
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {t("aff.payout.processedIn")}
              </p>

              <button
                onClick={handleClose}
                className="w-full py-2.5 rounded-[5px] bg-gray-100 dark:bg-[#25252B] text-gray-900 dark:text-white text-sm font-inter-tight font-bold hover:bg-gray-200 dark:hover:bg-[#1A1A1A] transition-colors"
              >
                {t("aff.payout.close")}
              </button>
            </div>
          ) : (
            /* Form View */
            <div className="space-y-4">
              {/* Available Balance */}
              <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-4">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("aff.payout.availableBalance")}</div>
                <div className="text-2xl font-mono font-bold text-[#00FF9D]">
                  ${availableBalance.toFixed(4)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {t("aff.payout.allPendingNote")}
                </div>
              </div>

              {/* Destination Address */}
              <div>
                <label className="block text-xs font-inter-tight font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  {t("aff.payout.destinationLabel")}
                </label>
                <input
                  type="text"
                  value={destinationAddress}
                  onChange={(e) => {
                    setDestinationAddress(e.target.value.trim());
                    setError(null);
                  }}
                  placeholder="0x..."
                  className={`w-full px-3 py-2.5 rounded-[5px] bg-white dark:bg-[#0f0f12] border text-sm font-mono text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/50 transition-colors ${
                    destinationAddress && !isValidAddress
                      ? "border-red-400 dark:border-red-500"
                      : "border-gray-200 dark:border-[#1A1A1A]"
                  }`}
                />
                {destinationAddress && !isValidAddress && (
                  <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {t("aff.payout.invalidAddress")}
                  </p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 rounded-[5px] bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              {/* Info */}
              <div className="text-xs text-gray-400 space-y-1">
                <p>{t("aff.payout.note1")}</p>
                <p>{t("aff.payout.note2")}</p>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || !isValidAddress || availableBalance <= 0}
                className={`w-full py-2.5 rounded-[5px] text-sm font-inter-tight font-bold transition-colors flex items-center justify-center gap-2 ${
                  loading || !isValidAddress || availableBalance <= 0
                    ? "bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 cursor-not-allowed"
                    : "bg-[#CCFF00] hover:bg-[#D4FF33] text-[#0A0A0A]"
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("aff.payout.requesting")}
                  </>
                ) : (
                  t("aff.payout.cta", { amount: availableBalance.toFixed(4) })
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
